/**
 * usePermissions Hook
 *
 * Provides easy access to user permissions and CRUD permission checks
 *
 * Usage:
 * const { canView, canCreate, canEdit, canDelete, hasPermission } = usePermissions('/users');
 *
 * if (canEdit) {
 *   // Show edit button
 * }
 */

import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { Permission } from '../types/permission.types';

export interface PermissionChecks {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  hasAnyPermission: boolean;
  hasPermission: (action: 'view' | 'create' | 'edit' | 'delete') => boolean;
  getPermission: () => Permission | undefined;
}

/**
 * Hook to check permissions for a specific menu path
 *
 * @param menuPath - The menu path to check permissions for (e.g., '/users', '/dashboard/news')
 * @returns Object with permission check functions and boolean flags
 */
export const usePermissions = (menuPath: string): PermissionChecks => {
  const { permissions, user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const checks = useMemo(() => {
    // Superadmin has all permissions
    if (isSuperAdmin) {
      return {
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
        hasAnyPermission: true,
      };
    }

    // Find permission for this menu path
    const permission = permissions?.find(p => p.menuPath === menuPath);

    if (!permission) {
      return {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        hasAnyPermission: false,
      };
    }

    return {
      canView: permission.canView === true,
      canCreate: permission.canCreate === true,
      canEdit: permission.canEdit === true,
      canDelete: permission.canDelete === true,
      hasAnyPermission:
        permission.canView === true ||
        permission.canCreate === true ||
        permission.canEdit === true ||
        permission.canDelete === true,
    };
  }, [menuPath, permissions, isSuperAdmin]);

  const hasPermission = (action: 'view' | 'create' | 'edit' | 'delete'): boolean => {
    if (isSuperAdmin) return true;

    switch (action) {
      case 'view':
        return checks.canView;
      case 'create':
        return checks.canCreate;
      case 'edit':
        return checks.canEdit;
      case 'delete':
        return checks.canDelete;
      default:
        return false;
    }
  };

  const getPermission = (): Permission | undefined => {
    if (isSuperAdmin) {
      // Return a full-access permission object for superadmin
      return {
        menuPath,
        menuText: '',
        canView: true,
        canCreate: true,
        canEdit: true,
        canDelete: true,
      };
    }
    return permissions?.find(p => p.menuPath === menuPath);
  };

  return {
    ...checks,
    hasPermission,
    getPermission,
  };
};

/**
 * Hook to check permissions for multiple menu paths
 * Useful when a component needs access to multiple resources
 *
 * @param menuPaths - Array of menu paths to check
 * @returns Map of menuPath to PermissionChecks
 */
export const useMultiplePermissions = (menuPaths: string[]): Map<string, PermissionChecks> => {
  const { permissions, user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const pathsKey = JSON.stringify(menuPaths);

  return useMemo(() => {
    const permissionsMap = new Map<string, PermissionChecks>();

    menuPaths.forEach(menuPath => {
      if (isSuperAdmin) {
        permissionsMap.set(menuPath, {
          canView: true,
          canCreate: true,
          canEdit: true,
          canDelete: true,
          hasAnyPermission: true,
          hasPermission: () => true,
          getPermission: () => ({
            menuPath,
            menuText: '',
            canView: true,
            canCreate: true,
            canEdit: true,
            canDelete: true,
          }),
        });
      } else {
        const permission = permissions?.find(p => p.menuPath === menuPath);

        if (!permission) {
          permissionsMap.set(menuPath, {
            canView: false,
            canCreate: false,
            canEdit: false,
            canDelete: false,
            hasAnyPermission: false,
            hasPermission: () => false,
            getPermission: () => undefined,
          });
        } else {
          const checks = {
            canView: permission.canView === true,
            canCreate: permission.canCreate === true,
            canEdit: permission.canEdit === true,
            canDelete: permission.canDelete === true,
            hasAnyPermission:
              permission.canView === true ||
              permission.canCreate === true ||
              permission.canEdit === true ||
              permission.canDelete === true,
          };

          permissionsMap.set(menuPath, {
            ...checks,
            hasPermission: (action: 'view' | 'create' | 'edit' | 'delete') => {
              switch (action) {
                case 'view': return checks.canView;
                case 'create': return checks.canCreate;
                case 'edit': return checks.canEdit;
                case 'delete': return checks.canDelete;
                default: return false;
              }
            },
            getPermission: () => permission,
          });
        }
      }
    });

    return permissionsMap;
  }, [pathsKey, permissions, isSuperAdmin]);
};
